import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Form, Button, Container, Row, Col, Alert, Spinner, InputGroup, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { EyeFill, EyeSlashFill } from 'react-bootstrap-icons'
import { amazonAPI } from '../api/amazonAPI'
import { brandAPI } from '../api/brandAPI'
import { formatErrorResponse } from '../utils/errorHandlers'
import { usePermissions } from '../contexts/PermissionContext'

function AmazonAccountUpdateScreen() {
    const { hasPermission, loading: permissionsLoading } = usePermissions()
    const { id } = useParams()
    const navigate = useNavigate()
    const [error, setError] = useState(null)
    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)
    const [testResult, setTestResult] = useState(null)
    const [testing, setTesting] = useState(false)
    const [gatewayTestResult, setGatewayTestResult] = useState(null)
    const [testingGateway, setTestingGateway] = useState(false)
    const [advertisingTestResult, setAdvertisingTestResult] = useState(null)
    const [testingAdvertising, setTestingAdvertising] = useState(false)
    const [fetchingProfiles, setFetchingProfiles] = useState(false)
    const [authorizing, setAuthorizing] = useState(false)
    const [availableProfiles, setAvailableProfiles] = useState([])
    const [clearGatewayApiKey, setClearGatewayApiKey] = useState(false)
    const [availableBrands, setAvailableBrands] = useState([])
    const [allAccounts, setAllAccounts] = useState([])
    const [account, setAccount] = useState({
        account_name: '',
        gateway_url: '',
        brand_ids: [],
        is_active: true,
        has_gateway_api_key: false,
        has_lwa_client_id: false,
        has_lwa_client_secret: false,
        has_refresh_token: false,
        has_adv_lwa_client_id: false,
        has_adv_lwa_client_secret: false,
        has_adv_refresh_token: false,
        advertising_profile_id: '',
    })
    const [credentials, setCredentials] = useState({
        gateway_api_key: '',
        lwa_client_id: '',
        lwa_client_secret: '',
        refresh_token: '',
        adv_lwa_client_id: '',
        adv_lwa_client_secret: '',
        adv_refresh_token: '',
    })
    const [showSecrets, setShowSecrets] = useState({
        gateway_api_key: false,
        lwa_client_id: false,
        lwa_client_secret: false,
        refresh_token: false,
        adv_lwa_client_id: false,
        adv_lwa_client_secret: false,
        adv_refresh_token: false,
    })

    useEffect(() => {
        async function fetchData() {
            try {
                const [brandsRes, accounts] = await Promise.all([
                    brandAPI.getAll(1, 1000, '', false),
                    amazonAPI.accounts.getAll(),
                ])
                setAvailableBrands(brandsRes.results || [])
                setAllAccounts(accounts || [])
            } catch (error) {
                console.error('Error fetching brands/accounts:', error)
            }
        }
        fetchData()
    }, [])

    useEffect(() => {
        async function fetchAccount() {
            try {
                const data = await amazonAPI.accounts.getById(id)
                if (!data.gateway_url) data.gateway_url = 'http://localhost:8001'
                setAccount(data)
            } catch (error) {
                console.error('Error fetching account:', error)
                setError('Failed to load Amazon account')
            } finally {
                setLoading(false)
            }
        }
        fetchAccount()
    }, [id])

    const brandToAccount = {}
    allAccounts.forEach(acc => {
        if (String(acc.id) !== String(id)) {
            (acc.brand_ids || []).forEach(brandId => {
                brandToAccount[brandId] = acc.account_name
            })
        }
    })

    const toggleBrand = (brandId) => {
        const current = account.brand_ids || []
        if (current.includes(brandId)) {
            setAccount({ ...account, brand_ids: current.filter(bid => bid !== brandId) })
        } else {
            setAccount({ ...account, brand_ids: [...current, brandId] })
        }
    }

    const submitHandler = async (e) => {
        e.preventDefault()
        setSubmitting(true)
        try {
            const updateData = {
                account_name: account.account_name,
                gateway_url: account.gateway_url || '',
                brand_ids: account.brand_ids || [],
                is_active: account.is_active,
            }
            if (clearGatewayApiKey) updateData.gateway_api_key = ''
            else if (credentials.gateway_api_key) updateData.gateway_api_key = credentials.gateway_api_key
            if (credentials.lwa_client_id) updateData.lwa_client_id = credentials.lwa_client_id
            if (credentials.lwa_client_secret) updateData.lwa_client_secret = credentials.lwa_client_secret
            if (credentials.refresh_token) updateData.refresh_token = credentials.refresh_token
            if (credentials.adv_lwa_client_id) updateData.adv_lwa_client_id = credentials.adv_lwa_client_id
            if (credentials.adv_lwa_client_secret) updateData.adv_lwa_client_secret = credentials.adv_lwa_client_secret
            if (credentials.adv_refresh_token) updateData.adv_refresh_token = credentials.adv_refresh_token
            updateData.advertising_profile_id = account.advertising_profile_id || ''
            updateData.sp_cache_ttl_minutes = account.sp_cache_ttl_minutes || 60

            await amazonAPI.accounts.update(id, updateData)
            navigate('/amazon-accounts')
        } catch (error) {
            setError(formatErrorResponse(error))
        } finally {
            setSubmitting(false)
        }
    }

    const buildTestPayload = () => {
        const payload = {}
        if (account.gateway_url) payload.gateway_url = account.gateway_url
        if (credentials.gateway_api_key) payload.gateway_api_key = credentials.gateway_api_key
        return payload
    }

    const handleTestGateway = async () => {
        setTestingGateway(true)
        setGatewayTestResult(null)
        try {
            const result = await amazonAPI.accounts.testGateway(id, buildTestPayload())
            setGatewayTestResult({ success: result.success, message: result.message })
        } catch (error) {
            setGatewayTestResult({ success: false, message: error.response?.data?.message || error.response?.data?.error || 'Gateway test failed' })
        } finally {
            setTestingGateway(false)
        }
    }

    const handleTestAdvertising = async () => {
        setTestingAdvertising(true)
        setAdvertisingTestResult(null)
        try {
            const result = await amazonAPI.accounts.testAdvertisingConnection(id, buildTestPayload())
            setAdvertisingTestResult({ success: result.success, message: result.message })
        } catch (error) {
            setAdvertisingTestResult({ success: false, message: error.response?.data?.message || error.response?.data?.error || 'Advertising connection test failed' })
        } finally {
            setTestingAdvertising(false)
        }
    }

    const handleFetchProfiles = async () => {
        setFetchingProfiles(true)
        setAdvertisingTestResult(null)
        try {
            const result = await amazonAPI.accounts.fetchAdvertisingProfiles(id, buildTestPayload())
            setAvailableProfiles(result.profiles || [])
            if (result.profiles?.length === 1 && !account.advertising_profile_id) {
                setAccount(prev => ({ ...prev, advertising_profile_id: result.profiles[0].profile_id }))
            }
        } catch (error) {
            setAdvertisingTestResult({ success: false, message: error.response?.data?.message || error.response?.data?.error || 'Failed to fetch profiles' })
        } finally {
            setFetchingProfiles(false)
        }
    }

    const handleAuthorizeAdvertising = async () => {
        setAuthorizing(true)
        setAdvertisingTestResult(null)
        try {
            const result = await amazonAPI.accounts.getAdvertisingOAuthUrl(id)
            if (result.oauth_url) {
                window.open(result.oauth_url, '_blank')
            }
        } catch (error) {
            setAdvertisingTestResult({
                success: false,
                message: error.response?.data?.error || error.response?.data?.message || 'Failed to get authorization URL'
            })
        } finally {
            setAuthorizing(false)
        }
    }

    const handleTestConnection = async () => {
        setTesting(true)
        setTestResult(null)
        try {
            const result = await amazonAPI.accounts.testConnection(id, buildTestPayload())
            setTestResult({ success: true, message: result.message || 'Connection successful' })
        } catch (error) {
            setTestResult({ success: false, message: error.response?.data?.message || error.response?.data?.error || 'Connection failed' })
        } finally {
            setTesting(false)
        }
    }

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this Amazon account? This action cannot be undone.')) {
            return
        }
        try {
            await amazonAPI.accounts.delete(id)
            navigate('/amazon-accounts')
        } catch (error) {
            setError(formatErrorResponse(error))
        }
    }

    const handleToggleActive = async () => {
        if (account.is_active) {
            if (!window.confirm('Are you sure you want to deactivate this account?')) {
                return
            }
        }
        try {
            const updateData = {
                account_name: account.account_name,
                is_active: !account.is_active,
            }
            const updated = await amazonAPI.accounts.update(id, updateData)
            setAccount(updated)
        } catch (error) {
            setError(formatErrorResponse(error))
        }
    }

    if (permissionsLoading || loading) {
        return (
            <Container className="py-3">
                <div className="text-center">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            </Container>
        )
    }

    if (!hasPermission('amazon.manage')) {
        return (
            <Container className="py-3">
                <Alert variant="danger">
                    You don&apos;t have permission to manage Amazon accounts.
                </Alert>
            </Container>
        )
    }

    return (
        <Container>
            <Row className="justify-content-left">
                <Col style={{ maxWidth: '730px' }}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h1 className="mb-0">Update Amazon Account</h1>
                        <Button
                            variant="outline-primary"
                            onClick={() => navigate('/amazon-accounts')}
                        >
                            <i className="fas fa-arrow-left me-2"></i>
                            Back to List
                        </Button>
                    </div>
                    <Form onSubmit={submitHandler}>
                        <Form.Group className="mb-4" controlId="account_name">
                            <Form.Label>Account Name*</Form.Label>
                            <Form.Control
                                type="text"
                                value={account.account_name}
                                onChange={(e) => setAccount({ ...account, account_name: e.target.value })}
                                required
                                placeholder="Enter account name"
                                disabled={!account.is_active}
                                style={{ opacity: account.is_active ? 1 : 0.6 }}
                                autoComplete="one-time-code"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="brands">
                            <Form.Label>Brands</Form.Label>
                            <div
                                className="d-flex flex-wrap gap-2 p-3 border rounded"
                                style={{ opacity: account.is_active ? 1 : 0.6 }}
                            >
                                {availableBrands.map((brand) => {
                                    const assignedTo = brandToAccount[brand._id]
                                    const isSelected = (account.brand_ids || []).includes(brand._id)
                                    if (assignedTo) {
                                        return (
                                            <OverlayTrigger
                                                key={brand._id}
                                                overlay={<Tooltip>Assigned to {assignedTo}</Tooltip>}
                                            >
                                                <Badge bg="secondary" style={{ cursor: 'not-allowed', opacity: 0.5 }}>
                                                    {brand.name}
                                                </Badge>
                                            </OverlayTrigger>
                                        )
                                    }
                                    return (
                                        <Badge
                                            key={brand._id}
                                            bg={isSelected ? 'success' : 'light'}
                                            style={{ cursor: account.is_active ? 'pointer' : 'default' }}
                                            onClick={() => account.is_active && toggleBrand(brand._id)}
                                        >
                                            {brand.name}
                                        </Badge>
                                    )
                                })}
                            </div>
                            <Form.Text className="text-muted">
                                Select brands sold on this Amazon account
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="gateway_url">
                            <Form.Label>Gateway URL</Form.Label>
                            <Form.Control
                                type="text"
                                value={account.gateway_url || ''}
                                onChange={(e) => setAccount({ ...account, gateway_url: e.target.value })}
                                placeholder="e.g. http://localhost:8001"
                                disabled={!account.is_active}
                                style={{ opacity: account.is_active ? 1 : 0.6 }}
                                autoComplete="one-time-code"
                                spellCheck="false"
                            />
                            <Form.Text className="text-muted">
                                URL of the Amazon Gateway for this account
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="gateway_api_key">
                            <Form.Label>
                                Gateway API Key
                                {account.has_gateway_api_key && !clearGatewayApiKey && (
                                    <>
                                        <small className="text-success ms-2">(configured)</small>
                                        <button
                                            type="button"
                                            className="btn btn-link text-danger ms-2 p-0"
                                            style={{ fontSize: 'inherit' }}
                                            onClick={() => {
                                                setClearGatewayApiKey(true)
                                                setCredentials({ ...credentials, gateway_api_key: '' })
                                            }}
                                        >
                                            remove
                                        </button>
                                    </>
                                )}
                                {clearGatewayApiKey && (
                                    <>
                                        <small className="text-danger ms-2">(will be removed on save)</small>
                                        <button
                                            type="button"
                                            className="btn btn-link text-primary ms-2 p-0"
                                            style={{ fontSize: 'inherit' }}
                                            onClick={() => setClearGatewayApiKey(false)}
                                        >
                                            undo
                                        </button>
                                    </>
                                )}
                            </Form.Label>
                            <InputGroup>
                                <Form.Control
                                    type={showSecrets.gateway_api_key ? "text" : "password"}
                                    value={credentials.gateway_api_key}
                                    onChange={(e) => {
                                        setClearGatewayApiKey(false)
                                        setCredentials({ ...credentials, gateway_api_key: e.target.value })
                                    }}
                                    placeholder={clearGatewayApiKey ? '' : 'Enter new value to replace'}
                                    disabled={!account.is_active || clearGatewayApiKey}
                                    style={{ opacity: account.is_active && !clearGatewayApiKey ? 1 : 0.6 }}
                                    autoComplete="one-time-code"
                                />
                                <Button
                                    variant="outline-secondary"
                                    tabIndex={-1}
                                    onClick={() => setShowSecrets({ ...showSecrets, gateway_api_key: !showSecrets.gateway_api_key })}
                                    disabled={clearGatewayApiKey}
                                >
                                    {showSecrets.gateway_api_key ? <EyeSlashFill /> : <EyeFill />}
                                </Button>
                            </InputGroup>
                            <Form.Text className="text-muted">
                                {clearGatewayApiKey ? '' : 'Leave blank to keep current value'}
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="lwa_client_id">
                            <Form.Label>
                                LWA Client ID
                                {account.has_lwa_client_id && (
                                    <small className="text-success ms-2">(configured)</small>
                                )}
                            </Form.Label>
                            <InputGroup>
                                <Form.Control
                                    type={showSecrets.lwa_client_id ? "text" : "password"}
                                    value={credentials.lwa_client_id}
                                    onChange={(e) => setCredentials({ ...credentials, lwa_client_id: e.target.value })}
                                    placeholder="Enter new value to replace"
                                    disabled={!account.is_active}
                                    style={{ opacity: account.is_active ? 1 : 0.6 }}
                                    autoComplete="one-time-code"
                                />
                                <Button
                                    variant="outline-secondary"
                                    tabIndex={-1}
                                    onClick={() => setShowSecrets({ ...showSecrets, lwa_client_id: !showSecrets.lwa_client_id })}
                                >
                                    {showSecrets.lwa_client_id ? <EyeSlashFill /> : <EyeFill />}
                                </Button>
                            </InputGroup>
                            <Form.Text className="text-muted">
                                Leave blank to keep current value
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="lwa_client_secret">
                            <Form.Label>
                                LWA Client Secret
                                {account.has_lwa_client_secret && (
                                    <small className="text-success ms-2">(configured)</small>
                                )}
                            </Form.Label>
                            <InputGroup>
                                <Form.Control
                                    type={showSecrets.lwa_client_secret ? "text" : "password"}
                                    value={credentials.lwa_client_secret}
                                    onChange={(e) => setCredentials({ ...credentials, lwa_client_secret: e.target.value })}
                                    placeholder="Enter new value to replace"
                                    disabled={!account.is_active}
                                    style={{ opacity: account.is_active ? 1 : 0.6 }}
                                    autoComplete="one-time-code"
                                />
                                <Button
                                    variant="outline-secondary"
                                    tabIndex={-1}
                                    onClick={() => setShowSecrets({ ...showSecrets, lwa_client_secret: !showSecrets.lwa_client_secret })}
                                >
                                    {showSecrets.lwa_client_secret ? <EyeSlashFill /> : <EyeFill />}
                                </Button>
                            </InputGroup>
                            <Form.Text className="text-muted">
                                Leave blank to keep current value
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="refresh_token">
                            <Form.Label>
                                Refresh Token
                                {account.has_refresh_token && (
                                    <small className="text-success ms-2">(configured)</small>
                                )}
                            </Form.Label>
                            <InputGroup>
                                <Form.Control
                                    type={showSecrets.refresh_token ? "text" : "password"}
                                    value={credentials.refresh_token}
                                    onChange={(e) => setCredentials({ ...credentials, refresh_token: e.target.value })}
                                    placeholder="Enter new value to replace"
                                    disabled={!account.is_active}
                                    style={{ opacity: account.is_active ? 1 : 0.6 }}
                                    autoComplete="one-time-code"
                                />
                                <Button
                                    variant="outline-secondary"
                                    tabIndex={-1}
                                    onClick={() => setShowSecrets({ ...showSecrets, refresh_token: !showSecrets.refresh_token })}
                                >
                                    {showSecrets.refresh_token ? <EyeSlashFill /> : <EyeFill />}
                                </Button>
                            </InputGroup>
                            <Form.Text className="text-muted">
                                Leave blank to keep current value
                            </Form.Text>
                        </Form.Group>

                        <hr className="my-4" />
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="mb-0">Advertising API Credentials</h5>
                            {account.has_adv_lwa_client_id && account.has_adv_lwa_client_secret && account.is_active && (
                                <Button
                                    variant="success"
                                    size="sm"
                                    onClick={handleAuthorizeAdvertising}
                                    disabled={authorizing}
                                >
                                    {authorizing ? (
                                        <>
                                            <Spinner animation="border" size="sm" className="me-1" />
                                            Opening...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-external-link-alt me-1"></i>
                                            Authorize with Amazon
                                        </>
                                    )}
                                </Button>
                            )}
                        </div>

                        <Form.Group className="mb-4" controlId="adv_lwa_client_id">
                            <Form.Label>
                                Advertising Client ID
                                {account.has_adv_lwa_client_id && (
                                    <small className="text-success ms-2">(configured)</small>
                                )}
                            </Form.Label>
                            <InputGroup>
                                <Form.Control
                                    type={showSecrets.adv_lwa_client_id ? "text" : "password"}
                                    value={credentials.adv_lwa_client_id}
                                    onChange={(e) => setCredentials({ ...credentials, adv_lwa_client_id: e.target.value })}
                                    placeholder="Enter new value to replace"
                                    disabled={!account.is_active}
                                    style={{ opacity: account.is_active ? 1 : 0.6 }}
                                    autoComplete="one-time-code"
                                />
                                <Button
                                    variant="outline-secondary"
                                    tabIndex={-1}
                                    onClick={() => setShowSecrets({ ...showSecrets, adv_lwa_client_id: !showSecrets.adv_lwa_client_id })}
                                >
                                    {showSecrets.adv_lwa_client_id ? <EyeSlashFill /> : <EyeFill />}
                                </Button>
                            </InputGroup>
                            <Form.Text className="text-muted">
                                Leave blank to keep current value
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="adv_lwa_client_secret">
                            <Form.Label>
                                Advertising Client Secret
                                {account.has_adv_lwa_client_secret && (
                                    <small className="text-success ms-2">(configured)</small>
                                )}
                            </Form.Label>
                            <InputGroup>
                                <Form.Control
                                    type={showSecrets.adv_lwa_client_secret ? "text" : "password"}
                                    value={credentials.adv_lwa_client_secret}
                                    onChange={(e) => setCredentials({ ...credentials, adv_lwa_client_secret: e.target.value })}
                                    placeholder="Enter new value to replace"
                                    disabled={!account.is_active}
                                    style={{ opacity: account.is_active ? 1 : 0.6 }}
                                    autoComplete="one-time-code"
                                />
                                <Button
                                    variant="outline-secondary"
                                    tabIndex={-1}
                                    onClick={() => setShowSecrets({ ...showSecrets, adv_lwa_client_secret: !showSecrets.adv_lwa_client_secret })}
                                >
                                    {showSecrets.adv_lwa_client_secret ? <EyeSlashFill /> : <EyeFill />}
                                </Button>
                            </InputGroup>
                            <Form.Text className="text-muted">
                                Leave blank to keep current value
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="adv_refresh_token">
                            <Form.Label>
                                Advertising Refresh Token
                                {account.has_adv_refresh_token && (
                                    <small className="text-success ms-2">(configured)</small>
                                )}
                            </Form.Label>
                            <InputGroup>
                                <Form.Control
                                    type={showSecrets.adv_refresh_token ? "text" : "password"}
                                    value={credentials.adv_refresh_token}
                                    onChange={(e) => setCredentials({ ...credentials, adv_refresh_token: e.target.value })}
                                    placeholder="Enter new value to replace"
                                    disabled={!account.is_active}
                                    style={{ opacity: account.is_active ? 1 : 0.6 }}
                                    autoComplete="one-time-code"
                                />
                                <Button
                                    variant="outline-secondary"
                                    tabIndex={-1}
                                    onClick={() => setShowSecrets({ ...showSecrets, adv_refresh_token: !showSecrets.adv_refresh_token })}
                                >
                                    {showSecrets.adv_refresh_token ? <EyeSlashFill /> : <EyeFill />}
                                </Button>
                            </InputGroup>
                            <Form.Text className="text-muted">
                                Leave blank to keep current value
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="advertising_profile_id">
                            <Form.Label>Advertising Profile ID</Form.Label>
                            <InputGroup>
                                {availableProfiles.length > 0 ? (
                                    <Form.Select
                                        value={account.advertising_profile_id || ''}
                                        onChange={(e) => setAccount({ ...account, advertising_profile_id: e.target.value })}
                                        disabled={!account.is_active}
                                        style={{ opacity: account.is_active ? 1 : 0.6 }}
                                    >
                                        <option value="">Select a profile...</option>
                                        {availableProfiles.map(p => (
                                            <option key={p.profile_id} value={p.profile_id}>
                                                {p.account_name} ({p.country_code}) - {p.profile_id}
                                            </option>
                                        ))}
                                    </Form.Select>
                                ) : (
                                    <Form.Control
                                        type="text"
                                        value={account.advertising_profile_id || ''}
                                        onChange={(e) => setAccount({ ...account, advertising_profile_id: e.target.value })}
                                        placeholder="Enter profile ID or use Fetch Profiles"
                                        disabled={!account.is_active}
                                        style={{ opacity: account.is_active ? 1 : 0.6 }}
                                        autoComplete="one-time-code"
                                        spellCheck="false"
                                    />
                                )}
                                <Button
                                    variant="outline-secondary"
                                    onClick={handleFetchProfiles}
                                    disabled={!account.is_active || fetchingProfiles}
                                >
                                    {fetchingProfiles ? (
                                        <>
                                            <Spinner animation="border" size="sm" className="me-1" />
                                            Fetching...
                                        </>
                                    ) : (
                                        'Fetch Profiles'
                                    )}
                                </Button>
                            </InputGroup>
                            <Form.Text className="text-muted">
                                Amazon Advertising profile ID for this marketplace
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="sp_cache_ttl_minutes">
                            <Form.Label>SP Cache TTL (minutes)</Form.Label>
                            <Form.Control
                                type="number"
                                min={5}
                                max={1440}
                                value={account.sp_cache_ttl_minutes || 60}
                                onChange={(e) => setAccount({ ...account, sp_cache_ttl_minutes: parseInt(e.target.value) || 60 })}
                                disabled={!account.is_active}
                                style={{ opacity: account.is_active ? 1 : 0.6 }}
                            />
                            <Form.Text className="text-muted">
                                How long cached SP campaign data remains fresh (default: 60 minutes)
                            </Form.Text>
                        </Form.Group>

                        {advertisingTestResult && (
                            <Alert
                                variant={advertisingTestResult.success ? 'success' : 'danger'}
                                onClose={() => setAdvertisingTestResult(null)}
                                dismissible
                            >
                                {advertisingTestResult.message}
                            </Alert>
                        )}

                        {gatewayTestResult && (
                            <Alert
                                variant={gatewayTestResult.success ? 'success' : 'danger'}
                                onClose={() => setGatewayTestResult(null)}
                                dismissible
                            >
                                {gatewayTestResult.message}
                            </Alert>
                        )}

                        {testResult && (
                            <Alert
                                variant={testResult.success ? 'success' : 'danger'}
                                onClose={() => setTestResult(null)}
                                dismissible
                            >
                                {testResult.message}
                            </Alert>
                        )}

                        {error && (
                            <Alert variant="danger" onClose={() => setError(null)} dismissible>
                                {error}
                            </Alert>
                        )}

                        {!account.is_active && (
                            <Alert variant="warning" className="mb-3">
                                This account is currently inactive. Activate it to make changes.
                            </Alert>
                        )}

                        <Row className="mt-3">
                            <Col>
                                {account.is_active ? (
                                    <>
                                        <Button variant="primary" type="submit" className="me-2" disabled={submitting}>
                                            {submitting ? 'Updating...' : 'Update'}
                                        </Button>
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={<Tooltip>Test connectivity to the gateway server only</Tooltip>}
                                        >
                                            <Button
                                                variant="secondary"
                                                onClick={handleTestGateway}
                                                className="me-2"
                                                disabled={testingGateway}
                                            >
                                                {testingGateway ? (
                                                    <>
                                                        <Spinner animation="border" size="sm" className="me-1" />
                                                        Testing...
                                                    </>
                                                ) : (
                                                    'Test Gateway'
                                                )}
                                            </Button>
                                        </OverlayTrigger>
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={<Tooltip>Test the full chain: gateway + Amazon SP-API authentication</Tooltip>}
                                        >
                                            <Button
                                                variant="info"
                                                onClick={handleTestConnection}
                                                className="me-2"
                                                disabled={testing}
                                            >
                                                {testing ? (
                                                    <>
                                                        <Spinner animation="border" size="sm" className="me-1" />
                                                        Testing...
                                                    </>
                                                ) : (
                                                    'Test SP-API'
                                                )}
                                            </Button>
                                        </OverlayTrigger>
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={<Tooltip>Test Advertising API credentials via gateway</Tooltip>}
                                        >
                                            <Button
                                                variant="info"
                                                onClick={handleTestAdvertising}
                                                className="me-2"
                                                disabled={testingAdvertising}
                                            >
                                                {testingAdvertising ? (
                                                    <>
                                                        <Spinner animation="border" size="sm" className="me-1" />
                                                        Testing...
                                                    </>
                                                ) : (
                                                    'Test Advertising'
                                                )}
                                            </Button>
                                        </OverlayTrigger>
                                        <Button
                                            variant="danger"
                                            onClick={handleToggleActive}
                                            className="me-2"
                                        >
                                            Deactivate
                                        </Button>
                                        <Button
                                            variant="outline-danger"
                                            onClick={handleDelete}
                                        >
                                            Delete
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <Button
                                            variant="success"
                                            onClick={handleToggleActive}
                                            className="me-2"
                                        >
                                            Activate
                                        </Button>
                                        <Button
                                            variant="outline-danger"
                                            onClick={handleDelete}
                                        >
                                            Delete
                                        </Button>
                                    </>
                                )}
                            </Col>
                        </Row>
                    </Form>
                </Col>
            </Row>
        </Container>
    )
}

export default AmazonAccountUpdateScreen
